<script setup lang="ts">
import LxcButton from "@lxc/app-device-common/src/components/LxcButton.vue";
import { storeToRefs } from "pinia";
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useUpdateDtwinLifeCycleState } from "~/modules/dtwin/composables/useUpdateDtwinLifeCycleState";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { DtwinLifeCycleState } from "~/modules/dtwin/models/DtwinLifeCycleState.enum";
import { DtwinListDisplayContext } from "~/modules/dtwin/models/DtwinListDisplayContext.enum";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";
import { getLifeCycleStateProperties } from "~/modules/dtwin/utils/getLifeCycleStateProperties";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import ILxcEllipse from "~icons/lxc-custom/ellipse";
import ILxcLightTrash2 from "~icons/lxc-light/trash-2";
import ILxcChevronDown from "~icons/lxc/chevron-down";

interface Props {
  dtwin: Dtwin;
  dtwinModel?: DtwinModel;
  context?: DtwinListDisplayContext;
}

const props = withDefaults(defineProps<Props>(), {
  dtwinModel: undefined,
  context: DtwinListDisplayContext.DTWINS,
});

const emits = defineEmits(["update"]);

const { t, te } = useI18n();

const { can } = useAcl();

const [confirmDialogVisible, toggleConfirmDialog] = useToggle();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const { error, isLoading, updateDtwinLifeCycleState } =
  useUpdateDtwinLifeCycleState({
    organizationCode: currentOrganization.value?.code,
  });

const actionButtonRef: Ref<typeof LxcButton | undefined> = ref();

const lifeCycleStateLabel = computed(
  () => getLifeCycleStateProperties(props.dtwin.lifeCycleState, t, te).label!,
);

const onCommand = (command: DtwinLifeCycleState) => {
  if (command === DtwinLifeCycleState.DISABLED) {
    toggleConfirmDialog(true);
  } else {
    onUpdateDtwinLifeCycleState(command);
  }
};

const lifeCycleStateOptions: ComputedRef<
  { command?: string; content: string; iconColor: string }[]
> = computed(() => {
  // get the possible transition from the current life cycle state of the device
  const transition = props.dtwinModel?.lifeCycle.transitions.find(
    (transition) => transition.from === props.dtwin.lifeCycleState,
  );

  if (transition) {
    const transitionDestinations = transition.to.filter((to) => {
      // filter on authorized UI transition destinations only
      if (to.includes("uiHidden=true")) {
        return false;
      }

      // filter transition destinations which match the previous life cycle state RSQL condition
      const previousLifeCycleStateCondition =
        "${twin.previousLifeCycleState}==";
      if (
        to.includes(previousLifeCycleStateCondition) &&
        !to.includes(
          `${previousLifeCycleStateCondition}'${props.dtwin.previousLifeCycleState}'`,
        )
      ) {
        return false;
      }

      return true;
    });

    return (
      transitionDestinations
        // extract the life cycle state enum corresponding to the transition destination
        .map((to) => {
          const substringEnd = to.indexOf("(");

          const lifeCycleState: string = to.substring(
            0,
            substringEnd !== -1 ? substringEnd : undefined,
          );

          return lifeCycleState;
        })
        // build life cycle state options
        .map((lifeCycleState) => {
          return {
            command: lifeCycleState,
            content: getLifeCycleStateProperties(lifeCycleState, t, te).label!,
            iconColor: getLifeCycleStateProperties(lifeCycleState).tailwindCSS,
          };
        })
    );
  }

  return [];
});

async function onUpdateDtwinLifeCycleState(
  newLifeCycleState: DtwinLifeCycleState,
) {
  await updateDtwinLifeCycleState(newLifeCycleState, props.dtwin.uid);

  if (error.value) {
    error.value.notify(NotificationKey.error);
  } else {
    showNotificationSuccess(t(NotificationKey.success));
    emits("update", {
      lifeCycleState: newLifeCycleState,
      previousLifeCycleState: props.dtwin.lifeCycleState,
    });
  }
  toggleConfirmDialog(false);
}

const canUpdateLifeCycleState = computed(
  () =>
    lifeCycleStateOptions.value.length &&
    [
      DtwinListDisplayContext.DTWIN_DETAIL,
      DtwinListDisplayContext.DTWINS,
    ].includes(props.context),
);

const buttonClasses = computed(() => {
  const buttonClasses = [];

  // adapt the button style on list contexts only
  if (
    [DtwinListDisplayContext.DTWINS, DtwinListDisplayContext.FLEET].includes(
      props.context,
    )
  ) {
    buttonClasses.push("!text-sm", "!px-2", "!py-2", "!font-medium");
  }

  if (!canUpdateLifeCycleState.value) {
    buttonClasses.push("!cursor-default", "!pointer-events-none");
  }

  return buttonClasses;
});
</script>

<template>
  <div>
    <lxc-button
      v-if="can(ACLRoles.CAN_WRITE_DEVICE)"
      ref="actionButtonRef"
      html-type="button"
      type="tertiary"
      :title="lifeCycleStateLabel"
      :icon="ILxcEllipse"
      :icon-class="`${
        getLifeCycleStateProperties(props.dtwin.lifeCycleState).tailwindCSS
      } !w-2.5 !h-2.5`"
      :disabled="isLoading"
      :is-loading="isLoading"
      :class="buttonClasses"
      @click.prevent.stop
    >
      <div class="flex gap-2 content-center items-center">
        <span>{{ lifeCycleStateLabel }}</span>
        <i-lxc-chevron-down
          v-if="
            !isLoading &&
            lifeCycleStateOptions.length &&
            context &&
            context !== DtwinListDisplayContext.FLEET
          "
          class="w-5 h-5"
        />
      </div>
    </lxc-button>

    <lxc-dropdown-menu
      v-if="actionButtonRef?.buttonRef && canUpdateLifeCycleState"
      :offset="4"
      :element="actionButtonRef?.buttonRef"
      @command="onCommand"
    >
      <lxc-dropdown-item
        v-for="(item, index) in lifeCycleStateOptions"
        :key="index"
        :command="item.command"
        class="min-w-48"
      >
        <div class="flex gap-2 items-center" :title="item.content">
          <i-lxc-ellipse :class="item.iconColor" />
          <span>{{ item.content }}</span>
        </div>
      </lxc-dropdown-item>
    </lxc-dropdown-menu>
  </div>

  <!-- Confirm modal on disable -->
  <confirm-modal
    :is-dialog-visible="confirmDialogVisible"
    :title="t('dtwins.action.confirm.title')"
    :description="t('dtwins.action.confirm.description')"
    :cancel-label="t('button.cancel')"
    :ok-label="t('dtwins.action.confirm.disable')"
    :icon="ILxcLightTrash2"
    icon-color-theme="error"
    color-theme="danger"
    :is-loading="isLoading"
    @confirm="onUpdateDtwinLifeCycleState(DtwinLifeCycleState.DISABLED)"
    @cancel="toggleConfirmDialog(false)"
  />
</template>
